import {
  Button,
  Center,
  Spacer,
  Spinner,
  Stack,
  StackDirection,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SubmitButton from "components/SubmitButton";
import {
  RequestProcessingModal,
  RequestErrorModals,
} from "components/RequestModals";
import useContractDetails from "hooks/api/contracts/useContractDetails";
import useCheckRequiredFieldsFilled from "hooks/useCheckRequiredFieldsFilled";
import { useCreateContractOrderRequest } from "hooks/api/requestOrder";
import { formatDateForApi } from "services/apiHelpers";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import { getDateAtMidnight, NO_MORE_ORDERS_THRESHOLD } from "util/constants";
import { requestOrderSchema } from "./requestOrderSchema";
import {
  NumberOfOrders,
  RequestOrderFormInputs,
  RequestOrderFormInputProps,
} from "./RequestOrderFormInputs";
import {
  ConfirmRequestedOrder,
  InvalidQuantityRequestedAlert,
  ReleaseNumberSuccessMessage,
} from "./modals";

export type ContractOrderRequest = {
  shipFrom: SelectOption | null;
  mode: SelectOption | null;
  salesTerms: SelectOption | null;
  shipTo: SelectOption | null;
  quantityPerOrder: number;
  requestedShipDate: Date | undefined;
  poNumber: string | undefined;
  numberOfOrders: number;
};

const buttonDirection = mobileStyleBreakpoint<StackDirection>(
  "column-reverse",
  "row"
) as StackDirection;
const footerDirection = mobileStyleBreakpoint<StackDirection>(
  "column",
  "row"
) as StackDirection;
const footerSpacing = mobileStyleBreakpoint(3, 8);
const minButtonWidth = mobileStyleBreakpoint("100%", "140px");

const RequestOrderForm = ({
  minShipDate,
  maxShipDate,
  contractNumber,
  unitOfMeasure,
  quantityRemainingToOrder,
  itemCode,
  customerName,
  customerCode,
  onClose = () => {
    return;
  },
}: {
  onClose?(): void;
  contractNumber: number | undefined;
  quantityRemainingToOrder: number;
  itemCode: string;
  customerName: string;
  customerCode: string;
} & RequestOrderFormInputProps) => {
  const {
    onOpen: onOpenRequestModal,
    isOpen: isOpenRequestModal,
    onClose: onCloseRequestModal,
  } = useDisclosure();
  const [failureCount, setFailureCount] = useState(0);

  const methods = useForm<ContractOrderRequest>({
    defaultValues: {
      requestedShipDate: minShipDate,
      quantityPerOrder: 22,
      poNumber: "",
      numberOfOrders: 1,
    },
    resolver: yupResolver(requestOrderSchema(minShipDate, maxShipDate)),
    mode: "all",
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    watch,
    reset: resetForm,
  } = methods;

  const quantityPerOrder = watch("quantityPerOrder");
  const numberOfOrders = watch("numberOfOrders");
  const totalRequestedQuantity = quantityPerOrder * numberOfOrders;
  const contractMaxQuantity =
    quantityRemainingToOrder + NO_MORE_ORDERS_THRESHOLD;
  const quantityOverContract = totalRequestedQuantity - contractMaxQuantity;

  const {
    mutateAsync: createRequestOrder,
    data: orderRequestCreationData,
    isError,
    error,
    reset: resetCreateContractOrderRequest,
  } = useCreateContractOrderRequest(customerCode, contractNumber);

  const onResetAndClose = useCallback(() => {
    onCloseRequestModal();
    onClose();
    resetForm();
    resetCreateContractOrderRequest();
  }, [
    onClose,
    onCloseRequestModal,
    resetCreateContractOrderRequest,
    resetForm,
  ]);

  const onCreateRequestOrder: SubmitHandler<ContractOrderRequest> = useCallback(
    (data) => {
      if (totalRequestedQuantity > contractMaxQuantity) {
        return;
      }
      return createRequestOrder({
        shipFrom: data.shipFrom?.value ?? "",
        mode: data.mode?.value ?? "",
        salesTerms: data.salesTerms?.value ?? "",
        shipTo: data.shipTo?.value ?? "",
        quantityPerOrder: data.quantityPerOrder,
        requestedShipDate: data.requestedShipDate
          ? formatDateForApi(data.requestedShipDate)
          : "",
        poNumber: data.poNumber ?? "",
        numberOfOrders: data.numberOfOrders,
        itemCode: itemCode,
        unitOfMeasureCode: unitOfMeasure,
      })
        .then(() => {
          setFailureCount(0);
        })
        .catch(() => {
          setFailureCount((_failureCount) => _failureCount + 1);
        });
    },
    [
      totalRequestedQuantity,
      contractMaxQuantity,
      createRequestOrder,
      itemCode,
      unitOfMeasure,
    ]
  );

  const onShowConfirmationModal: SubmitHandler<ContractOrderRequest> =
    useCallback(() => {
      resetCreateContractOrderRequest();
      setFailureCount(0);
      onOpenRequestModal();
    }, [resetCreateContractOrderRequest, onOpenRequestModal]);

  const showConfirmation =
    !isSubmitting &&
    !isError &&
    !orderRequestCreationData &&
    !(totalRequestedQuantity > contractMaxQuantity);

  const requiredFieldsFilled = useCheckRequiredFieldsFilled(
    watch([
      "shipFrom",
      "mode",
      "salesTerms",
      "shipTo",
      "quantityPerOrder",
      "requestedShipDate",
      "numberOfOrders",
    ])
  );

  return (
    <>
      {totalRequestedQuantity > contractMaxQuantity && (
        <InvalidQuantityRequestedAlert
          isOpen={isOpenRequestModal}
          onClose={onCloseRequestModal}
          quantityOverContract={quantityOverContract}
        />
      )}
      {isSubmitting && !isError && (
        <RequestProcessingModal isOpen={isOpenRequestModal}>
          Your order is being processed. This may take up to 1 minute, your page
          will refresh when complete.
        </RequestProcessingModal>
      )}
      {orderRequestCreationData && (
        <ReleaseNumberSuccessMessage
          isOpen={isOpenRequestModal}
          onClose={onResetAndClose}
          releaseNumber={
            orderRequestCreationData
              ? (Object.values(orderRequestCreationData) as number[])
              : []
          }
          customerName={customerName}
        />
      )}
      {showConfirmation && (
        <ConfirmRequestedOrder
          isOpen={isOpenRequestModal}
          onClose={onCloseRequestModal}
          numberOfOrders={numberOfOrders}
          quantityPerOrder={quantityPerOrder}
          unitOfMeasure={unitOfMeasure}
          customerName={customerName}
          onSubmit={handleSubmit(onCreateRequestOrder)}
        />
      )}
      {isError && (
        <RequestErrorModals
          isOpen={isOpenRequestModal}
          onClose={onCloseRequestModal}
          onRetry={handleSubmit(onCreateRequestOrder)}
          failureCount={failureCount}
          onReset={onResetAndClose}
          error={error}
          customerName={customerName}
        />
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onShowConfirmationModal)}>
          <Stack spacing={4}>
            <RequestOrderFormInputs
              minShipDate={minShipDate}
              maxShipDate={maxShipDate}
              unitOfMeasure={unitOfMeasure}
            />
            <Stack spacing={footerSpacing} direction={footerDirection}>
              <Spacer />
              <NumberOfOrders unitOfMeasure={unitOfMeasure} />
              <Stack direction={buttonDirection} spacing={3} mt={3}>
                <Button
                  colorScheme="gray"
                  variant="secondary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <SubmitButton
                  isDisabled={isOpenRequestModal || !requiredFieldsFilled}
                  minW={minButtonWidth}
                >
                  Request Order
                </SubmitButton>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};

const todaysDate = getDateAtMidnight();

const RequestOrderFormDataLoader = ({
  onClose = () => {
    return;
  },
}: {
  onClose?(): void;
}) => {
  const { data: contract, isLoading } = useContractDetails();

  if (isLoading || !contract) {
    return (
      <Center flex={1} minH="200px">
        <Spinner />
      </Center>
    );
  }

  const contractStartShipDate =
    contract?.shipStartDate instanceof Date
      ? getDateAtMidnight(contract.shipStartDate)
      : todaysDate;
  const minShipDate =
    todaysDate > contractStartShipDate ? todaysDate : contractStartShipDate;
  const maxShipDate =
    contract?.shipEndDate instanceof Date ? contract?.shipEndDate : undefined;

  return (
    <RequestOrderForm
      minShipDate={minShipDate}
      maxShipDate={maxShipDate}
      customerCode={contract.customerCode}
      customerName={contract.customerName}
      contractNumber={contract.contractNumber}
      unitOfMeasure={contract.unitOfMeasure ?? "ST"}
      quantityRemainingToOrder={contract.quantityRemainingToOrder ?? 0}
      itemCode={contract.itemCode ?? ""}
      onClose={onClose}
    />
  );
};

export default RequestOrderFormDataLoader;
